import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useMemo, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getHouseAccountBalances } from 'services/balances.service';
import { IHouseAccountBalance, minimumBalanceAmount } from 'constants/balance.constants';
import { formatDate, formatNumber } from 'constants/global.constants';
import { getHouseBalances } from 'state/reducers/balance.reducer';
import { SmallCard } from 'theme/elements/Cards';
import { CarouselComponent } from 'components/Carousel';
import { SwiperSlide } from 'swiper/react';
import { update } from 'lodash';
import _ from 'lodash';
import { formatValidDate } from 'utils/index';
import { timing } from 'constants/ui.constants';

export const HouseAccountBalances = () => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const [balanceSlides, setBalancesSlides] = useState<any>(null);
	const houseAccountBalances = useAppSelector(getHouseBalances);
	const houseAccountBalancePermission = hasPermission([permissionEnums.HOUSE_ACCOUNT_BALANCE]);

	useEffect(() => {
		if (houseAccountBalancePermission) {
			dispatch(getHouseAccountBalances());
		}
	}, [houseAccountBalancePermission]);

	const lastUpdate = useMemo(() => {
		if (houseAccountBalances?.length > 0) {
			const list = _.reverse(_.sortBy([...houseAccountBalances], 'updatedAt'));
			return `${formatValidDate(list[0]?.updatedAt, timing.DATE)} ${t('label.inHour')} ${formatValidDate(
				list[0]?.updatedAt,
				timing.TIME
			)}`;
		}
	}, [houseAccountBalances]);

	useEffect(() => {
		if (houseAccountBalances?.length > 0) {
			let gradientInitialPercent = 100;
			const cards = houseAccountBalances.map((item: IHouseAccountBalance, index: number) => {
				const splitNumber = formatNumber(item.amount);
				const gradientPercent = gradientInitialPercent;
				gradientInitialPercent = gradientInitialPercent === 70 ? 100 : gradientInitialPercent - 10;
				const card = (
					<SwiperSlide key={index}>
						<Grid
							item
							xs={8}
							sm={'auto'}
							sx={{ overflow: 'hidden', height: '180px', transform: 'scalex(-1)', direction: 'rtl' }}
						>
							<SmallCard
								key={index}
								styleObj={{
									minWidth: '225px',
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									background: `linear-gradient(91deg, rgb(20 76 71 / ${gradientPercent}%) 100%, ${theme.palette.root.basicDeeoGreen} 100%)`,
									color: 'white',
									position: 'relative',
								}}
							>
								<Stack spacing={3}>
									<Stack direction="row" alignItems="baseline" sx={{ transform: 'scalex(-1)' }}>
										<Typography variant="boldBody3">
											{parseInt(splitNumber[0]).toLocaleString()}
										</Typography>
										<Typography variant="boldBody5">.{splitNumber[1]}</Typography>
									</Stack>
									<Stack spacing={1} sx={{ transform: 'scalex(-1)', alignItems: 'end' }}>
										<Typography variant="boldBody6">{item.currency.symbol}</Typography>
										<Typography variant="body">{item.currency.code}</Typography>
									</Stack>
								</Stack>
								<>
									{item.amount < minimumBalanceAmount && (
										<img
											src="/icons/warning-orange.svg"
											style={{
												position: 'absolute',
												right: '7%',
												top: '17%',
											}}
										/>
									)}
								</>

								<img
									src="/icons/logo-brand-icon.svg"
									style={{
										filter: 'brightness(0) invert(1)',
										opacity: '0.3',
										position: 'absolute',
										right: '3%',
										bottom: '3%',
										transform: 'scalex(-1)',
									}}
								/>
							</SmallCard>
						</Grid>
					</SwiperSlide>
				);
				return card;
			});
			setBalancesSlides(cards);
		}
	}, [houseAccountBalances]);

	return (
		<Stack sx={{ position: 'relative' }}>
			<Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.common.black, marginBottom: 2 }}>
				{t('page.dashboard.balances')}
			</Typography>
			<Typography variant="body3" sx={{ color: theme.palette.common.black, marginBottom: 2 }}>
				{t('page.dashboard.lustUpdate')} {lastUpdate}
			</Typography>
			{balanceSlides?.length > 0 ? (
				<CarouselComponent>{balanceSlides}</CarouselComponent>
			) : (
				t('page.dashboard.noBalances')
			)}
		</Stack>
	);
};
